// TopBarCallButton — nút Gọi điện trên thanh trên cùng v2. DÙNG CHUNG SaaS + Core.
//
// Vì sao dời lên đây: lối vào bàn phím quay số vốn là NÚT NỔI góc phải-dưới, đúng góc mà
// chat dock đang chiếm (dock z-40 nên nút gọi phải chịu z-30 và bị dock đè khi mở hội
// thoại). Trên thanh này nút luôn thấy, không tranh chỗ với ai, và đứng cạnh avatar —
// đúng chỗ mắt tìm mấy chức năng cá nhân.
//
// HỢP ĐỒNG VỚI HOST — chỉ 1 thứ: `useVoipOptional()` từ `@host/voip`.
//   - Dùng bản OPTIONAL, KHÔNG dùng useVoip(): host chưa mount VoipProvider thì trả null
//     và nút tự ẩn. useVoip() sẽ NÉM, mà bọc try/catch quanh hook là phạm Rules of Hooks.
//   - `openDialer` nằm sẵn trong giá trị context của CẢ HAI host (VoipContext.jsx, kèm
//     comment "để cnv-ui gọi được mà không phải import bus riêng của từng host"). Nếu một
//     host nào đó chưa có, ta bắn thẳng event `voip:open-dialer` — cùng tên hằng số trong
//     voipDialerBus.js của cả hai — để nút không chết câm.
//
// Nút CHỈ có nghĩa khi user được gán máy nhánh (`extensionInfo`); không có thì ẩn hẳn,
// đúng như FloatingCallWidget vẫn làm (`if (!extensionInfo) return null`).
//
// Style mượn `.v2-topbar__icon` có sẵn (ui-v2.css mục 2) nên chỉ sống dưới
// <html data-ui="v2">; chấm trạng thái tô bằng inline style vì màu phải viền theo nền
// thanh (var(--v2-shell)) — Tailwind không có sẵn cặp này.

import { Phone } from 'lucide-react';
import { useVoipOptional } from '@host/voip';
import { useLingui } from '@lingui/react/macro';

const VOIP_OPEN_DIALER_EVENT = 'voip:open-dialer';

export default function TopBarCallButton() {
  const { t } = useLingui();
  const voip = useVoipOptional();

  // Host chưa bật tổng đài / user không có máy nhánh → không có gì để bấm.
  if (!voip?.extensionInfo) return null;

  const { extensionInfo, sipRegistered, sipStatus, callState, openDialer } = voip;
  const inCall = !!callState && callState !== 'idle';

  // Ba trạng thái PHẢI phân biệt được bằng mắt, vì cùng là "không gọi được ngay":
  //   đang kết nối (chờ chút) ≠ rớt đăng ký (bấm vào để nối lại). Gộp làm một là
  //   người dùng tưởng hỏng ngay giây đầu sau khi F5 (đã bị báo 27/07/2026).
  const dot = inCall ? '#38bdf8' : sipRegistered ? '#4ade80' : sipStatus === 'connecting' ? '#fbbf24' : '#94a3b8';
  const title = inCall
    ? t`Cuộc gọi đang diễn ra — mở khung gọi`
    : sipRegistered
      ? t`Gọi điện — máy nhánh ${extensionInfo.extension_number}`
      : sipStatus === 'connecting'
        ? t`Đang kết nối tổng đài…`
        : t`Tổng đài mất kết nối — mở để nối lại`;

  const handleClick = () => {
    if (typeof openDialer === 'function') { openDialer(); return; }
    try { window.dispatchEvent(new CustomEvent(VOIP_OPEN_DIALER_EVENT)); } catch { /* noop */ }
  };

  return (
    <button type="button" className="v2-topbar__icon" onClick={handleClick} title={title} aria-label={title}>
      <Phone size={16} />
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 4,
          right: 4,
          width: 7,
          height: 7,
          borderRadius: 999,
          background: dot,
          boxShadow: '0 0 0 2px var(--v2-shell)',
        }}
      />
    </button>
  );
}
